import React, { useState, useEffect } from 'react';
import { FiSave, FiRefreshCw, FiMail, FiMessageSquare, FiBell, FiSettings as FiSettingsIcon } from 'react-icons/fi';
import { getSettings, updateSetting } from '../services/settings';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import toast from 'react-hot-toast';

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    site_info: { name: 'Redsea Road', tagline: 'Private Transfers & Tours', logo_url: '' },
    contact_info: { phone: '+201286399333', email: 'info@redsearoad.tours', address: 'Hurghada, Egypt' },
    social_links: { facebook: '', instagram: '', twitter: '' },
    
    // Notification Settings
    notifications: {
      new_order_email: true,
      new_order_whatsapp: true,
      status_update_email: true,
      status_update_whatsapp: true,
      assignment_email: true,
      assignment_whatsapp: true,
      payment_received_email: true,
      payment_received_whatsapp: true,
    },
    
    // WhatsApp API Settings
    whatsapp_api: {
      phone_number_id: '',
      access_token: '',
      business_account_id: '',
      webhook_url: '',
      is_active: false,
    },
    
    // Email Settings
    email_settings: {
      smtp_host: 'smtp.gmail.com',
      smtp_port: 587,
      smtp_user: '',
      smtp_pass: '',
      from_email: 'noreply@redsearoad.tours',
      from_name: 'Redsea Road',
    },
    
    // Pricing Settings
    pricing: { currency: 'USD', tax_rate: 0, commission_rate: 20 },
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await getSettings();
      const settingsObj = {};
      data.data.forEach(setting => {
        settingsObj[setting.key] = setting.value;
      });
      setSettings(prev => ({ ...prev, ...settingsObj }));
      console.log('Loaded settings:', settingsObj);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save each category separately
      const categories = Object.keys(settings);
      for (const key of categories) {
        const value = settings[key];
        console.log(`Saving ${key}:`, value);
        await updateSetting(key, { value });
      }
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Helper for simple settings (site_info, contact_info, etc.)
  const handleChange = (category, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value,
      },
    }));
  };

  // Helper for nested settings (notifications, whatsapp_api, email_settings)
  const handleNestedChange = (category, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value,
      },
    }));
  };

  // Special handler for notification settings (since they are at root level)
  const handleNotificationChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [field]: value,
      },
    }));
  };

  // Special handler for whatsapp_api settings
  const handleWhatsAppChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      whatsapp_api: {
        ...prev.whatsapp_api,
        [field]: value,
      },
    }));
  };

  // Special handler for email_settings
  const handleEmailChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      email_settings: {
        ...prev.email_settings,
        [field]: value,
      },
    }));
  };

  const testWhatsAppConnection = async () => {
    try {
      const response = await fetch('/api/test/whatsapp', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ to: settings.contact_info?.phone || '+201286399333' })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('WhatsApp connection successful!');
      } else {
        toast.error(data.error || 'WhatsApp connection failed');
      }
    } catch (error) {
      console.error('WhatsApp test error:', error);
      toast.error('Failed to test WhatsApp connection');
    }
  };

  const testEmailConnection = async () => {
    try {
      const response = await fetch('/api/test/email', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ to: settings.contact_info?.email || 'info@redsearoad.tours' })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Email connection successful! Check your inbox');
      } else {
        toast.error(data.error || 'Email connection failed');
      }
    } catch (error) {
      console.error('Email test error:', error);
      toast.error('Failed to test email connection');
    }
  };

  if (loading) return <LoadingSpinner />;

  const tabs = [
    { id: 'general', label: 'General', icon: FiSettingsIcon },
    { id: 'notifications', label: 'Notifications', icon: FiBell },
    { id: 'whatsapp', label: 'WhatsApp API', icon: FiMessageSquare },
    { id: 'email', label: 'Email Settings', icon: FiMail },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition"
        >
          {saving ? <FiRefreshCw className="animate-spin mr-2" /> : <FiSave className="mr-2" />}
          {saving ? 'Saving...' : 'Save All Settings'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b mb-6">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center px-4 py-2 font-medium transition ${
              activeTab === tab.id
                ? 'border-b-2 border-primary-500 text-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="mr-2 w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* General Settings */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Site Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Site Name</label>
                <input
                  type="text"
                  value={settings.site_info?.name || ''}
                  onChange={(e) => handleChange('site_info', 'name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Tagline</label>
                <input
                  type="text"
                  value={settings.site_info?.tagline || ''}
                  onChange={(e) => handleChange('site_info', 'tagline', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-gray-600 mb-1">Logo URL</label>
                <input
                  type="text"
                  value={settings.site_info?.logo_url || ''}
                  onChange={(e) => handleChange('site_info', 'logo_url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Phone</label>
                <input
                  type="text"
                  value={settings.contact_info?.phone || ''}
                  onChange={(e) => handleChange('contact_info', 'phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  value={settings.contact_info?.email || ''}
                  onChange={(e) => handleChange('contact_info', 'email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-gray-600 mb-1">Address</label>
                <input
                  type="text"
                  value={settings.contact_info?.address || ''}
                  onChange={(e) => handleChange('contact_info', 'address', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Pricing Settings</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Default Currency</label>
                <select
                  value={settings.pricing?.currency || 'USD'}
                  onChange={(e) => handleChange('pricing', 'currency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Tax Rate (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={settings.pricing?.tax_rate || 0}
                  onChange={(e) => handleChange('pricing', 'tax_rate', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Default Commission (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={settings.pricing?.commission_rate || 20}
                  onChange={(e) => handleChange('pricing', 'commission_rate', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Settings */}
      {activeTab === 'notifications' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Notification Settings</h2>
          <p className="text-sm text-gray-500 mb-6">
            Choose which notifications to send and which channels to use.
          </p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-md font-medium text-gray-700 mb-3">New Order Notifications</h3>
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.notifications?.new_order_email === true}
                    onChange={(e) => handleNotificationChange('new_order_email', e.target.checked)}
                    className="mr-2"
                  />
                  <span>Email on New Order</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.notifications?.new_order_whatsapp === true}
                    onChange={(e) => handleNotificationChange('new_order_whatsapp', e.target.checked)}
                    className="mr-2"
                  />
                  <span>WhatsApp on New Order</span>
                </label>
              </div>
            </div>

            <div>
              <h3 className="text-md font-medium text-gray-700 mb-3">Status Update Notifications</h3>
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.notifications?.status_update_email === true}
                    onChange={(e) => handleNotificationChange('status_update_email', e.target.checked)}
                    className="mr-2"
                  />
                  <span>Email on Status Update</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.notifications?.status_update_whatsapp === true}
                    onChange={(e) => handleNotificationChange('status_update_whatsapp', e.target.checked)}
                    className="mr-2"
                  />
                  <span>WhatsApp on Status Update</span>
                </label>
              </div>
            </div>

            <div>
              <h3 className="text-md font-medium text-gray-700 mb-3">Assignment Notifications</h3>
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.notifications?.assignment_email === true}
                    onChange={(e) => handleNotificationChange('assignment_email', e.target.checked)}
                    className="mr-2"
                  />
                  <span>Email on Assignment</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.notifications?.assignment_whatsapp === true}
                    onChange={(e) => handleNotificationChange('assignment_whatsapp', e.target.checked)}
                    className="mr-2"
                  />
                  <span>WhatsApp on Assignment</span>
                </label>
              </div>
            </div>

            <div>
              <h3 className="text-md font-medium text-gray-700 mb-3">Payment Notifications</h3>
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.notifications?.payment_received_email === true}
                    onChange={(e) => handleNotificationChange('payment_received_email', e.target.checked)}
                    className="mr-2"
                  />
                  <span>Email on Payment Received</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.notifications?.payment_received_whatsapp === true}
                    onChange={(e) => handleNotificationChange('payment_received_whatsapp', e.target.checked)}
                    className="mr-2"
                  />
                  <span>WhatsApp on Payment Received</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp API Settings */}
      {activeTab === 'whatsapp' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">WhatsApp Business API</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number ID</label>
              <input
                type="text"
                value={settings.whatsapp_api?.phone_number_id || ''}
                onChange={(e) => handleWhatsAppChange('phone_number_id', e.target.value)}
                placeholder="Enter your WhatsApp Phone Number ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">Found in Meta for Developers dashboard</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Access Token</label>
              <input
                type="password"
                value={settings.whatsapp_api?.access_token || ''}
                onChange={(e) => handleWhatsAppChange('access_token', e.target.value)}
                placeholder="Enter your WhatsApp Access Token"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">Generate from Meta for Developers dashboard</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Account ID</label>
              <input
                type="text"
                value={settings.whatsapp_api?.business_account_id || ''}
                onChange={(e) => handleWhatsAppChange('business_account_id', e.target.value)}
                placeholder="Enter your WhatsApp Business Account ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Webhook URL</label>
              <input
                type="text"
                value={settings.whatsapp_api?.webhook_url || ''}
                onChange={(e) => handleWhatsAppChange('webhook_url', e.target.value)}
                placeholder="https://yourdomain.com/api/webhook/whatsapp"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">URL where Meta will send incoming messages</p>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.whatsapp_api?.is_active === true}
                  onChange={(e) => handleWhatsAppChange('is_active', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Enable WhatsApp API</span>
              </label>
            </div>

            <div className="pt-4 border-t">
              <button
                type="button"
                onClick={testWhatsAppConnection}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
              >
                Test WhatsApp Connection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Settings */}
      {activeTab === 'email' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Email Settings (SMTP)</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Host</label>
                <input
                  type="text"
                  value={settings.email_settings?.smtp_host || ''}
                  onChange={(e) => handleEmailChange('smtp_host', e.target.value)}
                  placeholder="smtp.gmail.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Port</label>
                <input
                  type="number"
                  value={settings.email_settings?.smtp_port || 587}
                  onChange={(e) => handleEmailChange('smtp_port', parseInt(e.target.value))}
                  placeholder="587"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Username</label>
                <input
                  type="text"
                  value={settings.email_settings?.smtp_user || ''}
                  onChange={(e) => handleEmailChange('smtp_user', e.target.value)}
                  placeholder="your-email@gmail.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Password</label>
                <input
                  type="password"
                  value={settings.email_settings?.smtp_pass || ''}
                  onChange={(e) => handleEmailChange('smtp_pass', e.target.value)}
                  placeholder="Your app password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Email</label>
                <input
                  type="email"
                  value={settings.email_settings?.from_email || ''}
                  onChange={(e) => handleEmailChange('from_email', e.target.value)}
                  placeholder="noreply@redsearoad.tours"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Name</label>
                <input
                  type="text"
                  value={settings.email_settings?.from_name || ''}
                  onChange={(e) => handleEmailChange('from_name', e.target.value)}
                  placeholder="Redsea Road"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div className="pt-4 border-t">
              <button
                type="button"
                onClick={testEmailConnection}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
              >
                Test Email Connection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
